import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  createStyles,
  Grid,
  Modal,
  Button,
  Text
} from '@mantine/core';
import { ChatSidebar } from './components/chat-sidebar';
import { ChatContent } from './components/chat-content';
import { ChatMsgSent } from './components/chat-msg-sent';
import { useChatDispatch, useChatState } from './context';
import { getUserData, getUserType } from '../../../config/userSession';
import { useDarkMode } from 'usehooks-ts';
import { deleteConversation } from '../../../graphql/mutations';
import { generateClient } from 'aws-amplify/api';
import { Amplify } from 'aws-amplify';
import { useGetUser } from '../../../hooks/use-get-user';
import { useNavigate } from 'react-router-dom';
import Banner from '../../../assets/images/editlistBanner.png';

const useStyles = createStyles((theme) => ({
  root: {
    background: 'rgba(224,244,245,0.5)',

    backgroundImage: `url(${Banner})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    height: '80vh' // Fixed height for full viewport
  },
  sidebar: {
    backgroundColor: theme.colors.gray[1],
    borderRight: `1px solid ${theme.colors.gray[3]}`,
    height: '85%',
    overflowY: 'auto'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    height: '85%',
    backgroundColor: theme.colors.white,
    overflow: 'hidden'
  },
  messageBox: {
    borderTop: `1px solid ${theme.colors.gray[3]}`,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.gray[0]
  },
  chatContent: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing.md
  },
  menu: {
    position: 'absolute',
    top: '50px',
    right: '20px',
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: '8px',
    boxShadow: '0px 4px 8px rgba(0,0,0,0.1)',
    zIndex: 1000,
    padding: '10px',
    width: '150px'
  },
  menuItem: {
    padding: '8px 12px',
    cursor: 'pointer',
    borderBottom: '1px solid #eee',
    '&:last-child': {
      borderBottom: 'none'
    },
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  }
}));

export const Assistant = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const userType = getUserType();
  const userData = useGetUser();
  const popupRef = useRef(null);
  const client = generateClient();
  const ASSISTANTACCESS = userData?.user?.UserAttributes?.find(
    (attr) => attr.Name === 'custom:assistant_access'
  )?.Value;
  const navigate = useNavigate();

  useEffect(() => {
    if (
      ASSISTANTACCESS !== 'APPROVED' &&
      userData?.user?.UserAttributes?.length > 0
    ) {
      navigate(`/${userType.toLowerCase()}/`);
    }
  }, [userData]);
  Amplify.configure({
    API: {
      GraphQL: {
        endpoint: process.env.REACT_APP_API_URI,
        region: process.env.REACT_APP_AWS_REGION,
        defaultAuthMode: 'apiKey',
        apiKey: process.env.REACT_APP_APPSYNC_API_KEY
      }
    }
  });

  const { classes } = useStyles();
  const dispatch = useChatDispatch();
  const { selectedChat } = useChatState();
  const userDetails = getUserData();
  useEffect(() => {
    dispatch({ type: 'SET_USER_ID', payload: userDetails.email });
  }, [dispatch, userDetails]);
  const darkMode = useDarkMode(false);
  const { isDarkMode } = useDarkMode();

  const titleParts = selectedChat?.title?.split('at ');
  const rawDate = titleParts?.length > 1 ? titleParts[1] : null;

  const formattedDate = new Date(rawDate).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  useEffect(() => {
    function handleClickOutside(event) {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setPopupOpen(false);
      }
    }
    if (popupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popupOpen]);

  const formattedTitle = `Conversation at ${formattedDate}`;
  const handleDelete = async () => {
    console.log('Deleting chat:', selectedChat?.id);
    const id = selectedChat?.id;
    try {
      const data = await client.graphql({
        query: deleteConversation,
        variables: { input: { id } }
      });
      console.log(data);
      if (data?.data.deleteConversation) {
        dispatch({ type: 'DELETED_CONVERSATION', payload: true });
        dispatch({ type: 'SET_SELECTED_CHAT', payload: null });
        setConfirmDelete(false);
      }
    } catch (error) {
      dispatch({ type: 'DELETED_CONVERSATION', payload: false });
      setConfirmDelete(false);
      console.error('Error deleting conversation:', error);
    }
  };
  return (
    <Container fluid className={classes.root}>
      <Text
        align="center"
        size="xl"
        weight={700}
        style={{
          padding: '15px',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          borderBottom: '1px solid #ddd',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px'
        }}
      >
        TripleNet AI Chatbot
      </Text>
      <Grid gutter={0} style={{ margin: 0, height: '100%' }}>
        {/* Sidebar */}
        <Grid.Col span={12} md={3} lg={3} className={classes.sidebar}>
          <ChatSidebar
            isMobileSidebarOpen={sidebarOpen}
            onSidebarClose={() => setSidebarOpen(false)}
          />
        </Grid.Col>

        {/* Chat Content */}
        <Grid.Col span={12} md={9} lg={9} className={classes.content}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: `1px solid ${!isDarkMode ? '#FFFFFF' : '#FFFFFF'}`,
              padding: '10px 20px',
              backgroundColor: !isDarkMode ? '#F1F5F9' : '#F1F5F9'
            }}
          >
            {' '}
            <div
              style={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                width={44}
                height={44}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                />
              </svg>

              {!selectedChat?.title?.startsWith('Conversation') ? (
                <p>{selectedChat?.title}</p>
              ) : (
                <p>
                  {formattedTitle === 'Conversation at January 1, 1970'
                    ? 'Chat'
                    : formattedTitle}
                </p>
              )}
            </div>
            <div></div>
            {formattedTitle !== 'Conversation at January 1, 1970' && (
              <div style={{ position: 'relative' }}>
                {' '}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  width={44}
                  height={44}
                  onClick={() => setPopupOpen(!popupOpen)}
                  style={{ cursor: 'pointer' }}
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
                  />
                </svg>
                {popupOpen && (
                  <div className={classes.menu} ref={popupRef}>
                    <div className={classes.menuItem}>
                      <Button onClick={() => setConfirmDelete(true)}>
                        Delete Chat
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <Box
            className={classes.chatContent}
            style={{
              height: '100%',
              backgroundColor: !isDarkMode ? '#FFFFFF' : '#FFFFFF',
              color: !isDarkMode ? '#FFFFFF' : '#000000'
            }}
          >
            <ChatContent />
          </Box>
          <Box
            className={classes.messageBox}
            style={{
              backgroundColor: !isDarkMode ? '#FFFFFF' : '#FFFFFF',
              color: !isDarkMode ? '#FFFFFF' : '#000000'
            }}
          >
            <ChatMsgSent />
          </Box>
        </Grid.Col>
      </Grid>
      <Modal
        opened={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Confirm Deletion"
        centered
      >
        <Text>Are you sure you want to delete this conversation?</Text>
        <Box mt="md" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="default"
            onClick={() => setConfirmDelete(false)}
            mr="sm"
          >
            Cancel
          </Button>
          <Button color="red" style={{ color: 'red' }} onClick={handleDelete}>
            Delete
          </Button>
        </Box>
      </Modal>
    </Container>
  );
};
